import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { PmsValueType } from 'lumen/utils/central-mapping-constants';
import dirtyRelationship, {
  type DirtyRelationshipModel,
} from 'lumen/utils/decorators/dirty-relationship';

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface CentralMappingCategory extends DirtyRelationshipModel, Model {}

@dirtyRelationship()
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class CentralMappingCategory extends Model {
  [Type] = 'central-mapping/category' as const;

  @belongsTo('central-mapping/category', { async: false, inverse: 'children' })
  declare parent: CentralMappingCategory | null;

  @hasMany('central-mapping/category', { async: false, inverse: 'parent' })
  declare children: HasMany<CentralMappingCategory>;

  @attr('boolean') declare isFixed: boolean;
  // When a hierarchy does not have fixed root categories, we create one to act as the parent.
  // It is not saved in the database or displayed in the UI, but it is used to group the root categories.
  @attr('boolean') declare isHiddenParent: boolean;
  @attr('string') declare name: string;
  @attr('string') declare valueType: PmsValueType;

  get depth(): number {
    if (this.isHiddenParent) {
      return -1;
    }
    return this.parent ? this.parent.depth + 1 : 0;
  }

  get hasChildren(): boolean {
    return this.undeletedChildren.length > 0;
  }

  get hasConfirmedName(): boolean {
    return this.name.length > 0;
  }

  get path(): CentralMappingCategory[] {
    const path: CentralMappingCategory[] = [this];
    let currentParent: CentralMappingCategory | null = this.parent;

    while (currentParent) {
      if (!currentParent.isHiddenParent) {
        path.push(currentParent);
      }
      currentParent = currentParent.parent;
    }

    return path.reverse();
  }

  get undeletedChildren(): CentralMappingCategory[] {
    return this.children.filter((category) => !category.isDeleted);
  }
}

export default CentralMappingCategory;
